import React, { useState, useEffect } from 'react';
import { getVarieties, createVariety, deleteVariety, getCropTypes } from '../../services/v2/adminService';
import TableHeader from '../../shared/components/tableHeader/TableHeader';

const LEVEL_OPTIONS = [
    { value: 1, label: '1 - Muy Baja' },
    { value: 2, label: '2 - Baja' },
    { value: 3, label: '3 - Media' },
    { value: 4, label: '4 - Alta' },
    { value: 5, label: '5 - Muy Alta' },
];

const VarietyAdmin = () => {
    const [varieties, setVarieties] = useState([]);
    const [cropTypes, setCropTypes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    const [formData, setFormData] = useState({
        name: '',
        cropTypeId: '',
        baseTemp: '',
        maxTemp: '',
        totalGddExpected: '',
        totalGddTolerance: '',
        targetChillPortions: '',
        expectedDays: '',
        expectedDaysTolerance: '',
        minDailyDli: '',
        optimumDailyDli: '',
        growthSpeedCategory: '',
        dliSensitivityLevel: '',
        harvestType: 'SINGLE'
    });

    const TABLE_HEADERS = ['Nombre Variedad', 'Tipo de Cultivo', 'Temp Base', 'GDD Total (Tol)', 'Días (Tol)', 'CHL Obj.', 'Cosecha', 'Acciones'];

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [vData, cData] = await Promise.all([getVarieties(), getCropTypes()]);
            setVarieties(vData || []);
            setCropTypes(cData || []);
        } catch (error) {
            console.error('Error fetching varieties data', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            await createVariety({
                ...formData,
                cropType: { id: formData.cropTypeId }
            });
            setFormData({
                name: '', 
                cropTypeId: '', 
                baseTemp: '', 
                maxTemp: '', 
                totalGddExpected: '', 
                totalGddTolerance: '',
                targetChillPortions: '', 
                expectedDays: '',
                expectedDaysTolerance: '',
                minDailyDli: '',
                optimumDailyDli: '',
                growthSpeedCategory: '',
                dliSensitivityLevel: '',
                harvestType: 'SINGLE'
            });
            fetchData();
        } catch (error) {
            console.error('Error creating variety', error);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Estás seguro de eliminar esta variedad?')) return;
        try {
            await deleteVariety(id);
            fetchData();
        } catch (error) {
            console.error('Error deleting variety', error);
        }
    };

    // const getLevelLabel = (value) => {
    //     const option = LEVEL_OPTIONS.find(opt => opt.value === Number(value));
    //     return option ? option.label : value;
    // };

    const inputClasses = "w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm";

    return (
        <div className="flex flex-col">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">Gestionar Variedades</h2>
            </div>

            {/* Complex Form for Variety */}
            <form onSubmit={handleCreate} className="mb-8 bg-gray-50 p-6 rounded-xl border border-gray-100 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-600 mb-4 uppercase tracking-wider">Nueva Variedad</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="lg:col-span-2">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Nombre de la Variedad</label>
                        <input type="text" name="name" placeholder="Ej: Hass, Bluecrop..." value={formData.name} onChange={handleChange} required className={inputClasses} />
                    </div>
                    <div className="lg:col-span-2">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Tipo de Cultivo</label>
                        <select name="cropTypeId" value={formData.cropTypeId} onChange={handleChange} required className={inputClasses}>
                            <option value="">Seleccione...</option>
                            {cropTypes.map(ct => <option key={ct.id} value={ct.id}>{ct.name}</option>)}
                        </select>
                    </div>
                    
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Temp. Base (°C)</label>
                        <input type="number" step="0.1" name="baseTemp" placeholder="Ej: 10" value={formData.baseTemp} onChange={handleChange} required className={inputClasses} />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Temp. Máx (°C)</label>
                        <input type="number" step="0.1" name="maxTemp" placeholder="Ej: 35" value={formData.maxTemp} onChange={handleChange} required className={inputClasses} />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">GDD Total Esp.</label>
                        <input type="number" step="0.1" name="totalGddExpected" placeholder="Ej: 1500" value={formData.totalGddExpected} onChange={handleChange} required className={inputClasses} />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Tolerancia GDD</label>
                        <input type="number" step="0.1" name="totalGddTolerance" placeholder="Ej: 100" value={formData.totalGddTolerance} onChange={handleChange} required className={inputClasses} />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Porciones Frío (CHL)</label>
                        <input type="number" step="0.1" name="targetChillPortions" placeholder="Ej: 400" value={formData.targetChillPortions} onChange={handleChange} required className={inputClasses} />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Días Totales Esp.</label>
                        <input type="number" name="expectedDays" placeholder="Ej: 120" value={formData.expectedDays} onChange={handleChange} required className={inputClasses} />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Tolerancia Días</label>
                        <input type="number" name="expectedDaysTolerance" placeholder="Ej: 10" value={formData.expectedDaysTolerance} onChange={handleChange} required className={inputClasses} />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">DLI Mínimo</label>
                        <input type="number" step="0.1" name="minDailyDli" placeholder="Ej: 8" value={formData.minDailyDli} onChange={handleChange} required className={inputClasses} />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">DLI Óptimo</label>
                        <input type="number" step="0.1" name="optimumDailyDli" placeholder="Ej: 25" value={formData.optimumDailyDli} onChange={handleChange} required className={inputClasses} />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Velocidad de Crecimiento</label>
                        <select name="growthSpeedCategory" value={formData.growthSpeedCategory} onChange={handleChange} required className={inputClasses}>
                            <option value="">Seleccione...</option>
                            {LEVEL_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Sensibilidad Lumínica (DLI)</label>
                        <select name="dliSensitivityLevel" value={formData.dliSensitivityLevel} onChange={handleChange} required className={inputClasses}>
                            <option value="">Seleccione...</option>
                            {LEVEL_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Tipo de Cosecha</label>
                        <select name="harvestType" value={formData.harvestType} onChange={handleChange} required className={inputClasses}>
                            <option value="SINGLE">Única (clavel, rosa...)</option>
                            <option value="MULTIPLE">Múltiple (arándano, fresa...)</option>
                        </select>
                    </div>

                    <div className="md:col-span-2 lg:col-span-4 flex justify-end mt-2">
                        <button 
                            type="submit" 
                            className="px-8 py-2 bg-blue-600 text-white font-bold rounded-md hover:bg-blue-700 transition-colors shadow-md flex items-center"
                        >
                            <span className="material-icons-outlined mr-2">add</span>
                            Guardar Variedad
                        </button>
                    </div>
                </div>
            </form>

            {isLoading ? (
                <div className="text-center py-10 text-gray-500">Cargando variedades...</div>
            ) : (
                <div className="relative overflow-x-auto shadow-sm border border-gray-200 rounded-lg">
                    <table className="w-full text-sm text-left text-gray-500">
                        <TableHeader headerOptions={TABLE_HEADERS} />
                        <tbody className="divide-y divide-gray-200">
                            {varieties.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="px-6 py-4 text-center text-gray-400">No hay registros</td>
                                </tr>
                            ) : (
                                varieties.map(v => (
                                    <tr key={v.id} className="bg-white hover:bg-gray-50 transition-colors">
                                        <th scope="row" className="px-6 py-4 font-bold text-gray-900 whitespace-nowrap">
                                            {v.name}
                                        </th>
                                        <td className="px-6 py-4 text-gray-600">{v.cropType?.name}</td>
                                        <td className="px-6 py-4 text-blue-600 font-medium">{v.baseTemp}°C</td>
                                        <td className="px-6 py-4 font-medium text-orange-600">
                                            {v.totalGddExpected} <span className="text-xs text-gray-400">(±{v.totalGddTolerance || 0})</span>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">
                                            {v.expectedDays} <span className="text-xs text-gray-400">(±{v.expectedDaysTolerance || 0})</span>
                                        </td>
                                        <td className="px-6 py-4 text-cyan-600">{v.targetChillPortions}</td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${v.harvestType === 'MULTIPLE' ? 'bg-indigo-50 text-indigo-600' : 'bg-gray-100 text-gray-600'}`}>
                                                {v.harvestType === 'MULTIPLE' ? 'Múltiple' : 'Única'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 flex gap-2">
                                            <button 
                                                onClick={() => handleDelete(v.id)}
                                                className="p-1 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                                            >
                                                <span className="material-icons-outlined">delete</span>
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default VarietyAdmin;
